import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { AccountApiService } from '../services/account-api.service';
import {
  accountSearchAPIActions,
  accountSearchPageActions,
  accountPageActions,
  accountPageAPIActions,
  teacherApiActions,
  teacherPageActions,
  studentsPageActions,
  studentsPageAPIActions,
  accountUsagePageActions,
  accountUsagePageAPIActions
} from './accounts.actions';
import * as selectors from './accounts.selectors';
import { Store } from '@ngrx/store';
import { DateService } from 'src/app/shared/services/date/date.service';
import { flipLanguage } from 'src/app/shared/functions/language';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as HubSelectors from 'src/app/store/hub.selector';
import { QueryResponse } from 'src/app/shared/models/query-response';
import { HttpErrorResponse } from '@angular/common/http';
import { AccountDetails } from './accounts.state';
import { Language } from 'src/app/shared/objects/languages.object';
import { environment } from 'src/environments/environment';
import { Subscription } from '../services/models/subscriptions.model';

@Injectable()
export class AccountEffects {
  private actions$ = inject(Actions);
  private accountApiService = inject(AccountApiService);
  private store = inject(Store);
  private dateService = inject(DateService);

  private _snackBar = inject(MatSnackBar);
  countryList$ = this.store.select(HubSelectors.getCountryList);

  searchAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        accountSearchPageActions.userSubmittedAccountSearch,
        accountSearchPageActions.userClickedLoadMoreAccounts,
        accountSearchPageActions.userWantsArchivedAccounts
      ),
      withLatestFrom(this.store.select(selectors.getAccountSearchForm)),
      switchMap(([, searchForm]) => {
        return this.accountApiService
          .searchAccounts(
            searchForm.searchTerm,
            searchForm.selectedProperty,
            searchForm.pageSize,
            searchForm.showAnonymizedAccounts
          )
          .pipe(
            map((response) => {
              if (response.isSuccess) {
                if (response.result.accounts.pageData.length > 0) {
                  return accountSearchAPIActions.searchAccountsSuccess({
                    searchResults: response.result.accounts
                  });
                } else {
                  return accountSearchAPIActions.searchAccountsFailure({
                    error: 'No results found for the search term'
                  });
                }
              }
              return accountSearchAPIActions.searchAccountsFailure({
                error: 'Search failed, Please try again.'
              });
            }),
            catchError((error) => {
              this.openSnackBar('Failed to load accounts, Please try again.');
              return of(
                accountSearchAPIActions.searchAccountsFailure({
                  error: error.message
                })
              );
            })
          );
      })
    )
  );

  loadAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        accountPageActions.initAccountPage,
        accountPageAPIActions.updateAccountDetailsSuccess,
        accountPageAPIActions.changeAccountLanguageSuccess,
        accountPageAPIActions.changeStatusAccountSuccess,
        accountPageAPIActions.activateAccountSuccess,
        accountPageAPIActions.deleteAccountSuccess,
        accountPageAPIActions.updateExternalSchoolIdentifierSuccess,
        accountPageAPIActions.updateAccountDetailsPartialSuccess
      ),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([, selectedAccountID]) =>
        this.accountApiService.loadAccount(selectedAccountID).pipe(
          map((response) => {
            if (response.isSuccess) {
              const locale = response.result.language == Language.EnglishUk ? 'uk' : 'us';
              const accountDetails: AccountDetails = {
                ...response.result,
                wordPressUrl:
                  response.result.wordpressId > 0
                    ? `${environment.wordpress.account.replace('<locale>', locale).replace('<wordpress_id>', response.result.wordpressId.toString())}`
                    : undefined
              };

              return accountPageAPIActions.loadAccountSuccess({
                account: accountDetails
              });
            } else {
              return accountPageAPIActions.loadAccountFailure({
                error: 'Failed to load account'
              });
            }
          }),
          catchError(() => {
            this.openSnackBar('Failed to load account, Please try again.');
            return of(
              accountPageAPIActions.loadAccountFailure({
                error: 'Failed to load account'
              })
            );
          })
        )
      )
    )
  );

  loadAccountSubscriptions = createEffect(() =>
    this.actions$.pipe(
      ofType(
        accountPageAPIActions.loadAccountSuccess,
        accountPageAPIActions.addSubscriptionSuccess,
        accountPageAPIActions.editSubscriptionSuccess,
        accountPageAPIActions.addSiteLicenceSuccess,
        accountPageAPIActions.addFreeTrialSuccess,
        accountPageAPIActions.addLiteracyOfferSuccess
      ),
      withLatestFrom(
        this.store.select(selectors.getSelectedAccountID),
        this.store.select(selectors.getSelectedAccountLanguage)
      ),
      switchMap(([, selectedAccountID, accountLanguage]) =>
        this.accountApiService.loadAccountSubscriptions(selectedAccountID).pipe(
          map((response) => {
            if (response.isSuccess) {
              const locale = accountLanguage == Language.EnglishUk ? 'uk' : 'us';
              const subscriptions: Subscription[] = response.result.subscriptions.map((subscription) => ({
                ...subscription,
                wordpressOrderUrl:
                  subscription.orderId > 0
                    ? `${environment.wordpress.orders.replace('<locale>', locale).replace('<order-id>', subscription.orderId.toString())}`
                    : undefined
              }));

              return accountPageAPIActions.loadAccountSubscriptionSuccess({
                subscriptions,
                licenceSummaries: response.result.licenseSummaries
              });
            } else {
              return accountPageAPIActions.loadAccountSubscriptionFailure({
                error: 'Failed to load account'
              });
            }
          })
        )
      )
    )
  );

  addAccountSubscription = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userAddedSubscription),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.addAccountSubscription(selectedAccountID, action.subscription).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Subscription added successfully');
              return accountPageAPIActions.addSubscriptionSuccess();
            } else {
              this.openSnackBar('Failed to add subscription, Please try again.');
              return accountPageAPIActions.addSubscriptionFailure({
                error: 'Failed to add subscription'
              });
            }
          })
        )
      )
    )
  );

  editAccountSubscription = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userEditedSubscription),
      switchMap((action) =>
        this.accountApiService.editAccountSubscription(action.subscription).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Subscription edited successfully');
              return accountPageAPIActions.editSubscriptionSuccess();
            } else {
              this.openSnackBar('Failed to edit subscription, Please try again.');
              return accountPageAPIActions.editSubscriptionFailure({
                error: 'Failed to edit subscription'
              });
            }
          })
        )
      )
    )
  );

  addAccountSiteLicence = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userAddedSiteLicence),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.addSiteLicence(selectedAccountID, action.subscription).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Site licence successfully');
              return accountPageAPIActions.addSiteLicenceSuccess();
            } else {
              this.openSnackBar('Failed to add site licence, Please try again.');
              return accountPageAPIActions.addSiteLicenceFailure({
                error: 'Failed to add site licence'
              });
            }
          })
        )
      )
    )
  );

  addFreeTrial = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userAddedFreeTrial),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.addFreeTrial(selectedAccountID, action.subscription).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Free trial added successfully');
              return accountPageAPIActions.addFreeTrialSuccess();
            } else {
              this.openSnackBar('Failed to add free trial, Please try again.');
              return accountPageAPIActions.addFreeTrialFailure({
                error: 'Failed to add free trial'
              });
            }
          })
        )
      )
    )
  );

  addLiteracyOffer = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userAddedLiteracyOffer),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.addLiteracyOffer(selectedAccountID, action.subscription).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Literacy offer added successfully');
              return accountPageAPIActions.addLiteracyOfferSuccess();
            } else {
              this.openSnackBar('Failed to add literacy offer, Please try again.');
              return accountPageAPIActions.addLiteracyOfferFailure({
                error: 'Failed to add literacy offer'
              });
            }
          })
        )
      )
    )
  );

  loadAccountNotes = createEffect(() =>
    this.actions$.pipe(
      ofType(
        accountPageAPIActions.loadAccountSuccess,
        accountPageAPIActions.addAccountNoteSuccess,
        accountPageAPIActions.addSiteLicenceSuccess,
        accountPageAPIActions.addSubscriptionSuccess,
        accountPageAPIActions.addFreeTrialSuccess,
        accountPageAPIActions.updateAccountNoteSuccess
      ),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([, selectedAccountID]) =>
        this.accountApiService.loadAccountNotes(selectedAccountID).pipe(
          map((response) => {
            if (response.isSuccess) {
              if (response.result) {
                const formattedNotes = response.result.accountHistory?.map((notes) => ({
                  ...notes,
                  createdAt: this.dateService.formatToLocalDate(notes.createdAt)
                }));

                return accountPageAPIActions.loadAccountNotesSuccess({
                  notes: formattedNotes
                });
              } else {
                return accountPageAPIActions.loadAccountNotesSuccess({
                  notes: []
                });
              }
            } else {
              return accountPageAPIActions.loadAccountNotesFailure({
                error: 'Failed to load account notes'
              });
            }
          })
        )
      )
    )
  );

  addAccountNote = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userSubmittedNote),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.submitAccountNote(selectedAccountID, action.note).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Note added to account successfully');
              return accountPageAPIActions.addAccountNoteSuccess();
            } else {
              this.openSnackBar('Failed to add account note, Please try again.');
              return accountPageAPIActions.addAccountNoteFailure({
                error: 'Failed to add account note'
              });
            }
          })
        )
      )
    )
  );

  updateAccountNote = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userEditedNote),
      switchMap((action) =>
        this.accountApiService.updateAccountNote(action.noteId, action.newNote).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Note updated successfully');
              return accountPageAPIActions.updateAccountNoteSuccess();
            } else {
              this.openSnackBar('Failed to update account note, Please try again.');
              return accountPageAPIActions.updateAccountNoteFailure({
                error: 'Failed to update account note'
              });
            }
          })
        )
      )
    )
  );

  exportAccount = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userClickedExportAccount),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([, selectedAccountID]) =>
        this.accountApiService.exportAccount(selectedAccountID).pipe(
          map((response) => {
            if (response) {
              const blob = new Blob([response], {
                type: 'text/csv;charset=utf-8;'
              });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Nessy_AccountExport_${this.dateService.getDateNowToLocaleDateString('en-GB')}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);

              return accountPageAPIActions.exportAccountSuccess();
            } else {
              this.openSnackBar('Failed to export account, Please try again.');
              return accountPageAPIActions.exportAccountFailure({
                error: 'Failed to export account'
              });
            }
          }),
          catchError(() =>
            of(
              accountPageAPIActions.exportAccountFailure({
                error: 'Failed to export account'
              })
            )
          )
        )
      )
    )
  );

  updateAccountDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userUpdatedAccountDetails),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.updateAccountDetails(selectedAccountID, action.changes).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Account details updated successfully');
              return accountPageAPIActions.updateAccountDetailsSuccess();
            } else {
              this.openSnackBar('Failed to update account details, Please try again.');
              return accountPageAPIActions.updateAccountDetailsFailure({
                error: 'Failed to update account details'
              });
            }
          }),
          catchError((error) => {
            let errorMessage = 'Failed to update customer account. Please try again.';
            const errors = error?.error?.errors ?? {};
            const backendMessage = errors['WordpressAccountUpsertFailed'];

            if (backendMessage?.includes('WordpressAccountUpsertFailed')) {
              errorMessage = 'This account was updated in ID, but failed to update in Wordpress.';
            }

            this.openSnackBar(errorMessage);

            return of(
              accountPageAPIActions.updateAccountDetailsPartialSuccess({
                error: backendMessage || errorMessage
              })
            );
          })
        )
      )
    )
  );

  resendWelcomeEmail = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userClickedResendWelcomeEmail),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.resendWelcomeEmail(selectedAccountID, action.isUDEmail).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Welcome email resent successfully');
              return accountPageAPIActions.resendWelcomeEmailSuccess();
            } else {
              this.openSnackBar('Failed to send welcome email, Please try again.');
              return accountPageAPIActions.resendWelcomeEmailFailure({
                error: 'Failed to resend welcome email'
              });
            }
          })
        )
      )
    )
  );

  resendHubSpotIntegrationMessage = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userClickedResendHubSpotIntegrationMessage),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([, selectedAccountID]) =>
        this.accountApiService.resendHubSpotIntegrationMessage(selectedAccountID).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('HubSpot Integration message resent successfully');
              return accountPageAPIActions.resendHubSpotIntegrationMessageSuccess();
            } else {
              this.openSnackBar('Failed to resend HubSpot Integration message, Please try again.');
              return accountPageAPIActions.resendHubSpotIntegrationMessageFailure({
                error: 'Failed to resend HubSpot Integration message'
              });
            }
          })
        )
      )
    )
  );

  changeAccountLanguage = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userChangedAccountLanguage),
      withLatestFrom(
        this.store.select(selectors.getSelectedAccountID),
        this.store.select(selectors.getSelectedAccountLanguage)
      ),
      switchMap(([, selectedAccountID, currentLanguage]) => {
        const newLanguage = flipLanguage(currentLanguage);
        return this.accountApiService.changeAccountLanguage(selectedAccountID, newLanguage).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Account language changed successfully');
              return accountPageAPIActions.changeAccountLanguageSuccess();
            } else {
              this.openSnackBar('Failed to change account language, Please try again.');
              return accountPageAPIActions.changeAccountLanguageFailure({
                error: 'Failed to change account language'
              });
            }
          })
        );
      })
    )
  );

  changeAccountStatus = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.updateAccountStatus),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.changeAccountStatus(selectedAccountID, action.disableAccount).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Account status updated successfully');
              return accountPageAPIActions.changeStatusAccountSuccess();
            } else {
              this.openSnackBar('Failed to update account status, Please try again.');
              return accountPageAPIActions.changeStatusAccountFailure({
                error: 'Failed to update account status'
              });
            }
          })
        )
      )
    )
  );

  deleteAccount = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.deleteAccount),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([, selectedAccountID]) =>
        this.accountApiService.deleteAccount(selectedAccountID).pipe(
          map((response: QueryResponse) => {
            if (response.isSuccess) {
              this.openSnackBar('Account has been deleted');
              return accountPageAPIActions.deleteAccountSuccess();
            } else {
              this.openSnackBar('Failed to delete account, Please try again.');
              return accountPageAPIActions.deleteAccountFailure({
                error: 'Failed to delete account'
              });
            }
          }),
          catchError((error: HttpErrorResponse) => {
            const errors = error.error?.errors;
            if (errors && errors['WordpressAccountDeletionFailed']) {
              this.openSnackBar('Account deleted in ID, but failed in wordpress');
            } else {
              this.openSnackBar('Failed to delete account, Please try again.');
            }
            return of(
              accountPageAPIActions.deleteAccountFailure({
                error: 'Failed to delete account'
              })
            );
          })
        )
      )
    )
  );

  loadTeachers = createEffect(() =>
    this.actions$.pipe(
      ofType(
        teacherPageActions.initTeacherPage,
        teacherApiActions.editOrAddTeacherSuccess,
        teacherApiActions.deleteTeacherSuccess
      ),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([, selectedAccountID]) => {
        return this.accountApiService.loadAccountTeachers(selectedAccountID, null).pipe(
          map((response) => {
            if (response.isSuccess) {
              return teacherApiActions.loadAccountTeachersSuccess({
                teachers: response.result
              });
            } else {
              return teacherApiActions.loadAccountTeachersFailure({
                error: 'Failed to load teachers'
              });
            }
          })
        );
      })
    )
  );

  searchTeachers = createEffect(() =>
    this.actions$.pipe(
      ofType(teacherPageActions.userSearchedTeachers),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.loadAccountTeachers(selectedAccountID, action.searchTerm).pipe(
          map((response) => {
            if (response.isSuccess) {
              return teacherApiActions.searchAccountTeachersSuccess({
                teachers: response.result
              });
            } else {
              return teacherApiActions.searchAccountTeachersFailure({
                error: 'Failed to load teachers'
              });
            }
          })
        )
      )
    )
  );

  userEditedOrAddedTeacher = createEffect(() =>
    this.actions$.pipe(
      ofType(teacherPageActions.userAddedTeacher, teacherPageActions.userEditedTeacher),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.editOrAddTeacher(selectedAccountID, action.teacher).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Teacher added / edited successfully');
              return teacherApiActions.editOrAddTeacherSuccess();
            } else {
              this.openSnackBar('Failed to add / edit teacher, Please try again.');
              return teacherApiActions.editOrAddTeacherFailure({
                error: 'Failed to add / edit teacher'
              });
            }
          })
        )
      )
    )
  );

  userDeletedTeacher = createEffect(() =>
    this.actions$.pipe(
      ofType(teacherPageActions.userDeletedTeacher),
      switchMap((action) =>
        this.accountApiService.deleteTeacher(action.userId).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Teacher deleted successfully');
              return teacherApiActions.deleteTeacherSuccess();
            } else {
              this.openSnackBar('Failed to delete teacher, Please try again.');
              return teacherApiActions.deleteTeacherFailure({
                error: 'Failed to delete teacher'
              });
            }
          })
        )
      )
    )
  );

  resendTeacherWelcomeEmail = createEffect(() =>
    this.actions$.pipe(
      ofType(teacherPageActions.userResentTeacherWelcomeEmail),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService.resendTeacherWelcomeEmail(selectedAccountID, action.userId, action.isUdTrainee).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Teacher welcome email resent successfully');
              return teacherApiActions.resendTeacherWelcomeEmailSuccess();
            } else {
              this.openSnackBar('Failed to resend teacher email, Please try again.');
              return teacherApiActions.resendTeacherWelcomeEmailFailure({
                error: 'Failed to resend welcome email to teacher'
              });
            }
          })
        )
      )
    )
  );

  loadStudents = createEffect(() =>
    this.actions$.pipe(
      ofType(
        studentsPageActions.initStudentsPage,
        studentsPageActions.userSearchedStudents,
        studentsPageActions.userSelectedStudentGroup,
        studentsPageActions.loadMoreStudents,
        studentsPageAPIActions.syncStudentLicensesSuccess,
        studentsPageAPIActions.syncAllAccountsStudentLicensesSuccess
      ),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) =>
        this.accountApiService
          .loadAccountStudents(selectedAccountID, action.pageSize, action.searchTerm, action.group)
          .pipe(
            map((response) => {
              if (response.isSuccess) {
                return studentsPageAPIActions.loadAccountStudentsSuccess({
                  students: response.result.studentProfiles
                });
              } else {
                this.openSnackBar('Failed to load students, Please try again.');
                return studentsPageAPIActions.loadAccountStudentsFailure({
                  error: 'Failed to load students'
                });
              }
            }),
            catchError(() => {
              this.openSnackBar('Failed to load students, Please try again.');
              return of(
                studentsPageAPIActions.loadAccountStudentsFailure({
                  error: 'Failed to load students'
                })
              );
            })
          )
      )
    )
  );

  loadStudentGroups = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsPageActions.initStudentsPage),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([, selectedAccountID]) =>
        this.accountApiService
          .loadStudentGroups(selectedAccountID)
          .pipe(
            map((response) => {
              if (response.isSuccess) {
                return studentsPageAPIActions.loadStudentGroupsSuccess({
                  studentGroups: response.result
                });
              } else {
                this.openSnackBar('Failed to load student groups, Please try again.');
                return studentsPageAPIActions.loadStudentGroupsFailure({
                  error: 'Failed to load student groups'
                });
              }
            }),
            catchError(() => {
              this.openSnackBar('Failed to load student groups, Please try again.');
              return of(
                studentsPageAPIActions.loadStudentGroupsFailure({
                  error: 'Failed to load student groups'
                })
              );
            })
          )
      )
    )
  );

  resyncStudentLicenses = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsPageActions.userSyncedStudentLicenses),
      withLatestFrom(this.store.select(selectors.getSelectedAccountStudents)),
      switchMap(([action, currentStudents]) =>
        this.accountApiService.syncStudentLicenses(action.studentId).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('Student licenses synced successfully');
              return studentsPageAPIActions.syncStudentLicensesSuccess({
                pageSize: currentStudents.filteredStudents.length,
                searchTerm: '',
                group: ''
              });
            } else {
              this.openSnackBar('Failed to sync student licenses, Please try again.');
              return studentsPageAPIActions.syncStudentLicensesFailure({
                error: 'Failed to sync student licenses'
              });
            }
          }),
          catchError(() => {
            this.openSnackBar('Failed to sync student licenses, Please try again.');
            return of(
              studentsPageAPIActions.syncStudentLicensesFailure({
                error: 'Failed to sync student licenses'
              })
            );
          })
        )
      )
    )
  );

  resyncAllAccountsStudentLicenses = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsPageActions.userSyncedAllAccountsStudentLicenses),
      withLatestFrom(
        this.store.select(selectors.getSelectedAccountID),
        this.store.select(selectors.getSelectedAccountStudents)
      ),
      switchMap(([, accountId, currentStudents]) =>
        this.accountApiService.syncAllAccountsStudentLicenses(accountId).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('All student licenses synced successfully');
              return studentsPageAPIActions.syncAllAccountsStudentLicensesSuccess({
                pageSize: currentStudents.filteredStudents.length,
                searchTerm: '',
                group: ''
              });
            } else {
              this.openSnackBar('Failed to sync all student licenses, Please try again.');
              return studentsPageAPIActions.syncAllAccountsStudentLicensesFailure({
                error: 'Failed to sync all student licenses'
              });
            }
          }),
          catchError(() => {
            this.openSnackBar('Failed to sync all student licenses, Please try again.');
            return of(
              studentsPageAPIActions.syncAllAccountsStudentLicensesFailure({
                error: 'Failed to sync all student licenses'
              })
            );
          })
        )
      )
    )
  );

  resetAccountPassword = createEffect(() =>
    this.actions$.pipe(
      ofType(teacherPageActions.userResetTeacherAccountPassword, accountPageActions.userResetAccountPassword),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) => {
        return this.accountApiService
          .resetAccountPassword(selectedAccountID, action.userId, action.isUdTrainee, action.isAdmin)
          .pipe(
            map((response) => {
              if (response.isSuccess) {
                this.openSnackBar('Password reset email sent successfully');
                return accountPageAPIActions.resetAccountPasswordSuccess();
              } else {
                this.openSnackBar('Failed to send password reset email, Please try again.');
                return accountPageAPIActions.resetAccountPasswordFailure({
                  error: 'Failed to send password reset email'
                });
              }
            }),
            catchError(() => {
              this.openSnackBar('Failed to send password reset email, Please try again.');
              return of(
                accountPageAPIActions.resetAccountPasswordFailure({
                  error: 'Failed to send password reset email'
                })
              );
            })
          );
      })
    )
  );

  updateExternalSchoolIdentifier = createEffect(() =>
    this.actions$.pipe(
      ofType(accountPageActions.userUpdatedSchoolIdentifier),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) => {
        return this.accountApiService.updateExternalSchoolIdentifier(selectedAccountID, action.changes).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('External school identifier updated successfully');
              return accountPageAPIActions.updateExternalSchoolIdentifierSuccess();
            } else {
              this.openSnackBar('Failed to update external school identifier, Please try again.');
              return accountPageAPIActions.updateExternalSchoolIdentifierFailure({
                error: 'Failed to update external school identifier'
              });
            }
          }),
          catchError(() => {
            this.openSnackBar('Failed to update external school identifier, Please try again.');
            return of(
              accountPageAPIActions.updateExternalSchoolIdentifierFailure({
                error: 'Failed to update external school identifier'
              })
            );
          })
        );
      })
    )
  );

  loadAccountUsage = createEffect(() =>
    this.actions$.pipe(
      ofType(
        accountUsagePageActions.initAccountUsagePage,
        accountUsagePageActions.resetDateFilter,
        accountUsagePageActions.applyDateFilter
      ),
      withLatestFrom(this.store.select(selectors.getSelectedAccountID)),
      switchMap(([action, selectedAccountID]) => {
        // Determine optional date filters
        const startDate = action.type === accountUsagePageActions.applyDateFilter.type ? action.startDate : undefined;
        const endDate = action.type === accountUsagePageActions.applyDateFilter.type ? action.endDate : undefined;

        return this.accountApiService.loadAccountUsage(selectedAccountID, startDate, endDate).pipe(
          map((response) => {
            if (response.isSuccess) {
              const updatedResponse = {
                ...response.result,
                applications: response.result.applications.map((applications) => ({
                  ...applications,
                  convertedTotalTime: this.dateService.formatSecondsToHMS(applications.totalTimeSeconds),
                  expiryDate: this.dateService.formatToLocalDate(applications.expiryDate)
                }))
              };

              return accountUsagePageAPIActions.loadAccountUsageSuccess({ usageData: updatedResponse });
            } else {
              this.openSnackBar('Failed to load account usage, Please try again.');
              return accountUsagePageAPIActions.loadAccountUsageFailure({
                error: 'Failed to load account usage'
              });
            }
          }),
          catchError(() => {
            this.openSnackBar('Failed to load account usage, Please try again.');
            return of(
              accountUsagePageAPIActions.loadAccountUsageFailure({
                error: 'Failed to load account usage'
              })
            );
          })
        );
      })
    )
  );

  openSnackBar(message: string) {
    this._snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'right'
    });
  }
}
